
import React, { useState, useRef, useEffect } from 'react';
import { getMobileOptimizedImageUrl } from '@/lib/mobile-performance';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileOptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  isLcp?: boolean;
  aspectRatio?: 'auto' | 'square' | 'video' | 'portrait' | 'landscape';
  objectPosition?: string;
}

interface NetworkConnection {
  effectiveType?: string;
  saveData?: boolean;
}

interface ExtendedNavigator extends Navigator {
  connection?: NetworkConnection;
}

export const MobileOptimizedImage: React.FC<MobileOptimizedImageProps> = ({
  src,
  alt,
  width = 400,
  height = 300,
  className = '',
  isLcp = false,
  aspectRatio = 'auto',
  objectPosition = 'center'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const isMobile = useIsMobile();
  
  // Aggressive mobile optimization
  const extendedNavigator = navigator as ExtendedNavigator;
  const isSlowConnection = extendedNavigator.connection?.effectiveType === 'slow-2g' || 
                          extendedNavigator.connection?.effectiveType === '2g' ||
                          extendedNavigator.connection?.saveData;
  
  const optimizedSrc = getMobileOptimizedImageUrl(src, isMobile && (isSlowConnection || isLcp));
  
  useEffect(() => {
    if (isLcp && optimizedSrc) {
      // Aggressive LCP optimization for mobile
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = optimizedSrc;
      link.fetchPriority = 'high';
      
      if (isMobile) {
        link.media = '(max-width: 768px)';
      }
      
      document.head.insertBefore(link, document.head.firstChild);
      
      // Start loading immediately
      const img = new Image();
      img.fetchPriority = 'high';
      img.loading = 'eager';
      img.decoding = isMobile ? 'async' : 'sync';
      img.src = optimizedSrc;
      
      img.onload = () => {
        setIsLoaded(true);
        if (performance.mark) {
          performance.mark('lcp-image-preloaded');
        }
      };
    }
  }, [optimizedSrc, isLcp, isMobile]);
  
  const handleLoad = () => {
    setIsLoaded(true);
    if (isLcp && performance.mark) {
      performance.mark('lcp-image-loaded');
      console.log('🚀 LCP image loaded for mobile');
    }
  };
  
  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };
  
  const aspectRatioClass = aspectRatio === 'auto' ? 'aspect-[4/3]' : 
                          aspectRatio === 'square' ? 'aspect-square' :
                          aspectRatio === 'video' ? 'aspect-video' :
                          aspectRatio === 'portrait' ? 'aspect-[3/4]' :
                          'aspect-[16/9]';
  
  // Mobile-specific sizing
  const mobileSizes = isLcp ? 
    "(max-width: 768px) 100vw, 50vw" : 
    "(max-width: 768px) 50vw, 33vw";
  
  return (
    <div className={`relative w-full overflow-hidden ${aspectRatioClass} ${className}`}>
      {!hasError ? (
        <img
          ref={imgRef}
          src={optimizedSrc}
          alt={alt}
          width={width}
          height={height}
          loading={isLcp ? 'eager' : 'lazy'}
          decoding={isLcp && !isMobile ? 'sync' : 'async'}
          fetchPriority={isLcp ? 'high' : 'low'}
          onLoad={handleLoad}
          onError={handleError}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: objectPosition,
            opacity: isLoaded ? 1 : 0,
            transition: isLcp && !isMobile ? 'opacity 0.1s ease' : 'opacity 0.2s ease',
            willChange: isLcp ? 'opacity' : 'auto'
          }}
          sizes={mobileSizes}
          data-lcp={isLcp ? 'true' : 'false'}
        />
      ) : (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center text-gray-400 text-xs bg-gray-100">
          Image unavailable
        </div>
      )}
      
      {/* Simplified loading state for mobile */}
      {!isLoaded && !hasError && (
        <div className={`absolute inset-0 ${!isMobile ? 'animate-pulse' : ''}`}>
          <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
        </div>
      )}
    </div>
  );
};
